import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Loader2, XCircle, ArrowLeft, Share2, Plus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Check = () => (
  <svg viewBox="0 0 52 52" className="w-16 h-16">
    <motion.path fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"
      d="M14 27 l8 8 l16 -18"
      initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5, ease: 'easeOut', delay: 0.2 }} />
  </svg>
);

const PaymentResult = ({ type }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(type === 'cancel' ? 'cancelled' : 'checking');
  const [data, setData] = useState({});
  const fired = useRef(false);

  useEffect(() => {
    if (type === 'cancel') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) { setState('error'); return; }
    let tries = 0, timer;
    const poll = async () => {
      tries += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setData(res.data);
        if (res.data.payment_status === 'paid') { setState('paid'); return; }
        if (['expired', 'failed'].includes(res.data.payment_status)) { setState('error'); return; }
      } catch (e) { /* retry */ }
      if (tries >= 8) { setState('timeout'); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [type]);

  useEffect(() => {
    if (state === 'paid' && !fired.current) {
      fired.current = true;
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.4 }, colors: ['#3ddc9a', '#0e9f6e', '#ffffff'] });
    }
  }, [state]);

  const share = async () => {
    const text = `Payment received: ${money(data.total)} via FLIX`;
    try { if (navigator.share) await navigator.share({ title: 'FLIX receipt', text }); else { await navigator.clipboard.writeText(text); } } catch (e) {}
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 max-w-md mx-auto text-center">
      {state === 'checking' && (<><Loader2 size={44} className="text-primary animate-spin" /><p className="mt-6 text-muted-foreground">Confirming payment…</p></>)}

      {state === 'paid' && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 18 }}
            className="w-24 h-24 rounded-full bg-primary/15 text-primary flex items-center justify-center mx-auto"><Check /></motion.div>
          <h1 className="font-heading text-3xl font-black mt-6">Payment received</h1>
          <p className="font-heading text-5xl font-black tracking-tight text-primary mt-2">{money(data.total)}</p>

          <div className="mt-6 rounded-3xl bg-card border border-border p-5 text-left">
            <p className="font-heading text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Receipt</p>
            <Row label="Amount" value={money(data.amount)} />
            {data.tax > 0 && <Row label="Tax" value={money(data.tax)} />}
            {data.tip > 0 && <Row label="Tip" value={money(data.tip)} />}
            {(data.tax > 0 || data.tip > 0) && <Row label="Subtotal" value={money(data.subtotal)} bold />}
            <Row label="Card Processing Fee" value={money((Number(data.fee) || 0) + (Number(data.processing_fee) || 0))} muted />
            <div className="border-t border-dashed border-border my-2" />
            <div className="flex justify-between items-center"><span className="font-heading font-bold">Total charged</span><span className="font-heading font-black text-primary">{money(data.total)}</span></div>
            <p className="text-[11px] text-primary font-medium mt-1.5">✓ Merchant receives the full sale amount.</p>
          </div>

          <div className="mt-5 flex gap-2">
            <button data-testid="share-receipt-button" onClick={share} className="flex-1 h-13 py-3.5 rounded-full bg-secondary text-foreground font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Share2 size={18} strokeWidth={2.5} /> Share</button>
            <button data-testid="new-sale-button" onClick={() => navigate('/app')} className="flex-1 h-13 py-3.5 rounded-full bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"><Plus size={18} strokeWidth={2.75} /> New sale</button>
          </div>
        </motion.div>
      )}

      {(state === 'cancelled' || state === 'error' || state === 'timeout') && (
        <>
          <div className="w-20 h-20 rounded-full bg-secondary flex items-center justify-center"><XCircle size={44} className="text-muted-foreground" /></div>
          <h1 className="font-heading text-2xl font-black mt-6">{state === 'cancelled' ? 'Payment cancelled' : state === 'timeout' ? 'Still processing' : 'Something went wrong'}</h1>
          <p className="text-muted-foreground text-sm mt-3">{state === 'timeout' ? 'Your payment is taking a moment. Check your dashboard shortly.' : 'No charge was made. You can try again.'}</p>
          <button data-testid="back-to-app-button" onClick={() => navigate('/app')} className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-full px-8 py-3.5 text-sm font-bold"><ArrowLeft size={16} /> Back to app</button>
        </>
      )}
    </div>
  );
};

const Row = ({ label, value, muted, bold }) => (
  <div className="flex justify-between text-sm py-0.5"><span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold text-foreground' : 'text-foreground/80'}>{label}</span><span className={muted ? 'text-muted-foreground' : bold ? 'font-heading font-bold text-foreground' : 'text-foreground/80'}>{value}</span></div>
);

export default PaymentResult;
